import { Link } from "react-router-dom";
import { ClipboardCheck, ArrowRight } from "lucide-react";
import { track } from "../lib/api";

/* Survey referral CTA — fresh leaf/forest green theme so all three partner
   CTAs are distinct (conveyancing = amber, broker = teal, survey = green)
   while sharing the same card system. MortgagePath is not a surveyor; it
   introduces users to a RICS-regulated survey provider and may receive a
   referral fee. */
const GREEN_BG = "#F0FDF4";
const GREEN_BORDER = "#22C55E";
const GREEN_BUTTON = "#16A34A";
const GREEN_BUTTON_HOVER = "#15803D";
const ICON_BADGE_BG = "#DCFCE7";

const buttonStyle = { background: GREEN_BUTTON, color: "#FFFFFF" };
const onButtonEnter = (e) => { e.currentTarget.style.background = GREEN_BUTTON_HOVER; };
const onButtonLeave = (e) => { e.currentTarget.style.background = GREEN_BUTTON; };

const InnerContent = () => (
  <>
    <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: GREEN_BUTTON }}>
      Survey quote
    </div>
    <h3 className="font-sora text-xl font-semibold text-[#0B1220] mt-1">
      Need a survey quote?
    </h3>
    <p className="mt-2 text-sm text-[#475569] leading-relaxed">
      Get a no-obligation quote from a RICS-regulated survey provider.
    </p>
  </>
);

const FootNote = () => (
  <p className="mt-3 text-xs text-[#475569] leading-relaxed">
    MortgagePath is not a surveyor and does not provide surveying advice. We may receive a referral fee
    if you choose to proceed.
  </p>
);

const IconBadge = ({ small = false }) => (
  <span
    className={`${small ? "w-8 h-8 rounded-lg" : "mt-0.5 w-10 h-10 rounded-xl"} grid place-items-center shrink-0`}
    style={{ background: ICON_BADGE_BG, color: GREEN_BUTTON }}
  >
    <ClipboardCheck className={small ? "w-4 h-4" : "w-5 h-5"} />
  </span>
);

const SurveyQuoteCTA = ({ source = "direct", variant = "boxed", className = "" }) => {
  const href = `/survey-quote?source=${encodeURIComponent(source)}`;
  const onCtaClick = () => {
    // Single event per physical click — cta_location captures placement.
    track("survey_cta_clicked", { cta_location: source, cta_text: "Request a survey quote", source });
  };

  // Fully clickable card variant for use inside guides.
  if (variant === "clickable-card") {
    return (
      <Link
        to={href}
        onClick={onCtaClick}
        className={`block group mt-6 rounded-2xl border p-5 hover:shadow-md transition-all ${className}`}
        style={{ background: GREEN_BG, borderColor: GREEN_BORDER }}
        data-testid={`survey-quote-cta-card-${source}`}
      >
        <div className="flex items-start gap-4">
          <IconBadge />
          <div className="flex-1">
            <InnerContent />
            <span
              className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm"
              style={buttonStyle}
              onMouseEnter={onButtonEnter}
              onMouseLeave={onButtonLeave}
            >
              Request a survey quote <ArrowRight className="w-4 h-4" />
            </span>
            <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
            <FootNote />
          </div>
        </div>
      </Link>
    );
  }

  // Subtle variant — toned down for Snapshot end screen where the CTA sits next
  // to the amber ConveyancingCTA subtle variant.
  if (variant === "subtle") {
    return (
      <div
        className={`rounded-2xl border p-5 ${className}`}
        style={{ background: "#F7FEF9", borderColor: "#86EFAC" }}
        data-testid={`survey-quote-cta-${source}`}
      >
        <div className="flex items-start gap-3">
          <IconBadge small />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: GREEN_BUTTON }}>
              Survey quote
            </div>
            <h3 className="font-sora text-base font-semibold text-[#0B1220] mt-1">
              Need a survey quote?
            </h3>
            <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">
              Get a no-obligation quote from a RICS-regulated survey provider.
            </p>
            <Link
              to={href}
              onClick={onCtaClick}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
              style={buttonStyle}
              onMouseEnter={onButtonEnter}
              onMouseLeave={onButtonLeave}
              data-testid={`survey-quote-cta-btn-${source}`}
            >
              Request a survey quote <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
            <p className="mt-2 text-[11px] text-[#94A3B8] leading-relaxed">
              MortgagePath is not a surveyor and does not provide surveying advice. We may receive a
              referral fee if you choose to proceed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default boxed CTA — same slate-blue palette, non-clickable card.
  return (
    <div
      className={`rounded-2xl border p-6 sm:p-7 ${className}`}
      style={{ background: GREEN_BG, borderColor: GREEN_BORDER }}
      data-testid={`survey-quote-cta-${source}`}
    >
      <div className="flex items-start gap-4">
        <IconBadge />
        <div className="flex-1">
          <InnerContent />
          <Link
            to={href}
            onClick={onCtaClick}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors"
            style={buttonStyle}
            onMouseEnter={onButtonEnter}
            onMouseLeave={onButtonLeave}
            data-testid={`survey-quote-cta-btn-${source}`}
          >
            Request a survey quote <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
          <FootNote />
        </div>
      </div>
    </div>
  );
};

export default SurveyQuoteCTA;
